import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { collection, query, where, orderBy, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase/app';
import { colors } from '../../styles/colors';

interface Report {
  id: string;
  reporterId: string;
  reporterNickname: string;
  targetId: string;
  targetNickname: string;
  reason: string;
  detail?: string;
  contentType?: string;
  contentPreview?: string;
  contentTimestamp?: number | null;
  status: string;
  adminReply?: string;
  createdAt: any;
}

const CONTENT_TYPE_LABELS: Record<string, { ko: string; en: string }> = {
  message: { ko: '채팅 메시지', en: 'Chat Message' },
  dm: { ko: '쪽지', en: 'DM' },
  board: { ko: '게시물', en: 'Board Post' },
  comment: { ko: '댓글', en: 'Comment' },
  map: { ko: '구글맵', en: 'Google Map' },
  nearby: { ko: '최근 사용자', en: 'Recent Users' },
  profile: { ko: '프로필', en: 'Profile' },
};

export default function ReportHistoryPage() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyingId, setReplyingId] = useState<string | null>(null);

  const isAdmin = user?.isAdmin ?? false;
  // ?view=received → admin's received reports view
  const isReceivedView = isAdmin && searchParams.get('view') === 'received';

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        let q;
        if (isReceivedView) {
          // Admin: all reports except own
          q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
        } else {
          // Regular user (or admin viewing own reports): only my reports
          q = query(collection(db, 'reports'), where('reporterId', '==', user.uid), orderBy('createdAt', 'desc'));
        }
        const snap = await getDocs(q);
        let results = snap.docs.map(d => ({ id: d.id, ...d.data() } as Report));
        if (isReceivedView) {
          results = results.filter(r => r.reporterId !== user.uid);
        }
        setReports(results);
      } catch {
        // Fallback without orderBy (missing index)
        const q = isReceivedView
          ? query(collection(db, 'reports'))
          : query(collection(db, 'reports'), where('reporterId', '==', user.uid));
        const snap = await getDocs(q);
        let results = snap.docs.map(d => ({ id: d.id, ...d.data() } as Report));
        if (isReceivedView) results = results.filter(r => r.reporterId !== user.uid);
        results.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
        setReports(results);
      }
      setLoading(false);
    })();
  }, [user?.uid, isReceivedView]);

  const handleReply = async (reportId: string) => {
    if (!replyText.trim()) return;
    await updateDoc(doc(db, 'reports', reportId), {
      adminReply: replyText.trim(),
      status: 'replied',
    });
    setReports(prev => prev.map(r => r.id === reportId ? { ...r, adminReply: replyText.trim(), status: 'replied' } : r));
    setReplyText('');
    setReplyingId(null);
  };

  const formatDate = (ts: any) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatContentTime = (ms: number | null | undefined) => {
    if (!ms) return '';
    const d = new Date(ms);
    return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
  };

  const reasonLabels: Record<string, string> = {
    spam: isKo ? '스팸/광고' : 'Spam',
    abuse: isKo ? '욕설/비하' : 'Abuse',
    sexual: isKo ? '성적 콘텐츠' : 'Sexual Content',
    fraud: isKo ? '사기/사칭' : 'Fraud',
    other: isKo ? '기타' : 'Other',
  };

  const title = isReceivedView
    ? (isKo ? '신고 접수 내역' : 'Received Reports')
    : (isKo ? '신고 내역' : 'Report History');

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: colors.background }}>
      <div className="flex items-center px-4 py-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <button onClick={() => navigate(-1)} className="mr-3 text-sm" style={{ color: colors.primary }}>← </button>
        <h1 className="text-base font-bold" style={{ color: colors.text }}>{title}</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <p className="mt-8 text-center text-sm" style={{ color: colors.textMuted }}>{isKo ? '로딩 중...' : 'Loading...'}</p>
        ) : reports.length === 0 ? (
          <p className="mt-8 text-center text-sm" style={{ color: colors.textMuted }}>{isKo ? '신고 내역이 없습니다.' : 'No reports.'}</p>
        ) : (
          reports.map(r => {
            const typeLabel = r.contentType
              ? (isKo ? CONTENT_TYPE_LABELS[r.contentType]?.ko : CONTENT_TYPE_LABELS[r.contentType]?.en) ?? r.contentType
              : null;
            return (
              <div key={r.id} className="mb-2 rounded-lg p-3" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <span className="text-sm font-bold" style={{ color: colors.text }}>
                      {isReceivedView ? (
                        <>
                          <button onClick={() => window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: r.reporterId, nickname: r.reporterNickname } }))}
                            className="hover:underline" style={{ color: colors.text }}>{r.reporterNickname}</button>
                          {' → '}
                          <button onClick={() => window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: r.targetId, nickname: r.targetNickname } }))}
                            className="hover:underline" style={{ color: colors.text }}>{r.targetNickname}</button>
                        </>
                      ) : (
                        <button onClick={() => window.dispatchEvent(new CustomEvent('open-profile', { detail: { uid: r.targetId, nickname: r.targetNickname } }))}
                          className="hover:underline" style={{ color: colors.text }}>{r.targetNickname}</button>
                      )}
                    </span>
                    <span className="ml-2 rounded px-1.5 py-0.5 text-[9px] font-bold"
                      style={{
                        backgroundColor: r.status === 'replied' ? `${colors.primary}20` : `${colors.warning}20`,
                        color: r.status === 'replied' ? colors.primary : colors.warning,
                      }}>
                      {r.status === 'replied' ? (isKo ? '답변 완료' : 'Replied') : (isKo ? '대기 중' : 'Pending')}
                    </span>
                  </div>
                  <span className="text-[10px]" style={{ color: colors.textMuted }}>{formatDate(r.createdAt)}</span>
                </div>
                <p className="text-xs" style={{ color: colors.textSecondary }}>
                  {isKo ? '사유' : 'Reason'}: {reasonLabels[r.reason] ?? r.reason}
                </p>
                {typeLabel && (
                  <p className="text-[10px] mt-0.5" style={{ color: colors.textMuted }}>
                    {isKo ? '경로' : 'Source'}: {typeLabel}
                  </p>
                )}
                {r.contentPreview && (
                  <div className="mt-1 rounded px-2 py-1" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
                    <p className="text-[10px] line-clamp-3" style={{ color: colors.textSecondary }}>{r.contentPreview}</p>
                    {r.contentTimestamp && (
                      <p className="mt-0.5 text-[9px]" style={{ color: colors.textMuted }}>
                        {isKo ? '콘텐츠 시간' : 'Content time'}: {formatContentTime(r.contentTimestamp)}
                      </p>
                    )}
                  </div>
                )}
                {r.detail && <p className="mt-1 text-xs" style={{ color: colors.textMuted }}>{r.detail}</p>}
                {r.adminReply && (
                  <div className="mt-2 rounded px-2 py-1.5" style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}30` }}>
                    <p className="text-[10px] font-bold" style={{ color: colors.primary }}>{isKo ? '관리자 답변' : 'Admin Reply'}</p>
                    <p className="text-xs" style={{ color: colors.text }}>{r.adminReply}</p>
                  </div>
                )}
                {isAdmin && replyingId === r.id && (
                  <div className="mt-2 flex gap-1">
                    <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                      placeholder={isKo ? '답변 입력...' : 'Reply...'}
                      className="flex-1 rounded border bg-transparent px-2 py-1 text-xs outline-none"
                      style={{ borderColor: colors.border, color: colors.text }} />
                    <button onClick={() => handleReply(r.id)} className="rounded px-2 py-1 text-xs font-bold text-white" style={{ backgroundColor: colors.primary }}>
                      {isKo ? '전송' : 'Send'}
                    </button>
                  </div>
                )}
                {isAdmin && replyingId !== r.id && (
                  <button onClick={() => { setReplyingId(r.id); setReplyText(r.adminReply ?? ''); }}
                    className="mt-1 text-[10px] font-bold" style={{ color: colors.primary }}>
                    {r.adminReply ? (isKo ? '답변 수정' : 'Edit Reply') : (isKo ? '답변하기' : 'Reply')}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
